<template>
  <div class="video-cropper">
    <!-- 比例選擇按鈕 -->
    <div class="ratio-selector">
      <button
        v-for="ratio in ratios"
        :key="ratio.value"
        @click="selectRatio(ratio)"
        :class="{'active': selectedRatio === ratio.value}"
        class="ratio-btn"
      >
        {{ ratio.label }}
      </button>
    </div>

    <!-- 影片預覽和裁切區域 -->
    <div class="preview-container" ref="container">
      <video
        ref="video"
        :src="videoUrl"
        @loadedmetadata="onVideoLoaded"
        :style="videoStyle"
        crossorigin="anonymous"
      ></video>

      <!-- 裁切框 -->
      <div
        v-if="videoLoaded"
        class="crop-overlay"
        :style="overlayStyle"
      >
        <div
          class="crop-box"
          :style="cropBoxStyle"
          @mousedown="startDrag"
        >
          <!-- 四個角的調整控制點 -->
          <div class="resize-handle nw" @mousedown.stop="startResize('nw', $event)"></div>
          <div class="resize-handle ne" @mousedown.stop="startResize('ne', $event)"></div>
          <div class="resize-handle sw" @mousedown.stop="startResize('sw', $event)"></div>
          <div class="resize-handle se" @mousedown.stop="startResize('se', $event)"></div>

          <!-- 裁切框內顯示尺寸 -->
          <div class="crop-info">
            {{ Math.round(cropBox.width) }} × {{ Math.round(cropBox.height) }}
          </div>
        </div>
      </div>
    </div>

    <!-- 操作按鈕 -->
    <div class="actions">
      <button @click="confirm" class="btn-confirm">確認裁切</button>
      <button @click="cancel" class="btn-cancel">取消</button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VideoCropper',
  props: {
    videoFile: {
      type: File,
      required: true
    }
  },
  data() {
    return {
      videoUrl: '',
      videoLoaded: false,
      videoWidth: 0,
      videoHeight: 0,
      displayWidth: 0,
      displayHeight: 0,
      cropBox: {
        x: 0,
        y: 0,
        width: 0,
        height: 0
      },
      isDragging: false,
      isResizing: false,
      resizeHandle: '',
      dragStart: { x: 0, y: 0 },
      cropBoxStart: { x: 0, y: 0, width: 0, height: 0 },
      ratios: [
        { label: '自訂', value: 'free' },
        { label: '1:1', value: '1:1' },
        { label: '9:16', value: '9:16' },
        { label: '16:9', value: '16:9' },
        { label: '4:5', value: '4:5' }
      ],
      selectedRatio: 'free',
      aspectRatio: null
    };
  },
  computed: {
    videoStyle() {
      return {
        width: this.displayWidth + 'px',
        height: this.displayHeight + 'px',
        display: 'block'
      };
    },
    overlayStyle() {
      return {
        width: this.displayWidth + 'px',
        height: this.displayHeight + 'px'
      };
    },
    cropBoxStyle() {
      if (!this.videoLoaded) return {};

      const scale = this.displayWidth / this.videoWidth;
      return {
        left: this.cropBox.x * scale + 'px',
        top: this.cropBox.y * scale + 'px',
        width: this.cropBox.width * scale + 'px',
        height: this.cropBox.height * scale + 'px'
      };
    }
  },
  mounted() {
    this.videoUrl = URL.createObjectURL(this.videoFile);
    document.addEventListener('mousemove', this.onDrag);
    document.addEventListener('mouseup', this.stopDrag);
  },
  beforeUnmount() {
    if (this.videoUrl) {
      URL.revokeObjectURL(this.videoUrl);
    }
    document.removeEventListener('mousemove', this.onDrag);
    document.removeEventListener('mouseup', this.stopDrag);
  },
  methods: {
    onVideoLoaded() {
      const video = this.$refs.video;
      this.videoWidth = video.videoWidth;
      this.videoHeight = video.videoHeight;

      // 設定顯示尺寸（最大 600px 寬）
      const maxWidth = 600;
      const ratio = Math.min(maxWidth / this.videoWidth, 1);
      this.displayWidth = this.videoWidth * ratio;
      this.displayHeight = this.videoHeight * ratio;

      // 初始化裁切框為全畫面
      this.cropBox = {
        x: 0,
        y: 0,
        width: this.videoWidth,
        height: this.videoHeight
      };

      this.videoLoaded = true;
    },

    selectRatio(ratio) {
      this.selectedRatio = ratio.value;

      if (ratio.value === 'free') {
        this.aspectRatio = null;
        return;
      }

      // 解析比例
      const [w, h] = ratio.value.split(':').map(Number);
      this.aspectRatio = w / h;

      // 根據比例調整裁切框
      const videoRatio = this.videoWidth / this.videoHeight;

      if (videoRatio > this.aspectRatio) {
        // 影片更寬，以高度為準
        this.cropBox.height = this.videoHeight;
        this.cropBox.width = this.videoHeight * this.aspectRatio;
      } else {
        // 影片更高，以寬度為準
        this.cropBox.width = this.videoWidth;
        this.cropBox.height = this.videoWidth / this.aspectRatio;
      }

      // 置中
      this.cropBox.x = (this.videoWidth - this.cropBox.width) / 2;
      this.cropBox.y = (this.videoHeight - this.cropBox.height) / 2;
    },

    startDrag(e) {
      this.isDragging = true;
      this.dragStart = {
        x: e.clientX,
        y: e.clientY
      };
      this.cropBoxStart = { ...this.cropBox };
      e.preventDefault();
    },

    startResize(handle, e) {
      this.isResizing = true;
      this.resizeHandle = handle;
      this.dragStart = {
        x: e.clientX,
        y: e.clientY
      };
      this.cropBoxStart = { ...this.cropBox };
      e.preventDefault();
    },

    onDrag(e) {
      if (!this.isDragging && !this.isResizing) return;

      const scale = this.displayWidth / this.videoWidth;
      const dx = (e.clientX - this.dragStart.x) / scale;
      const dy = (e.clientY - this.dragStart.y) / scale;

      if (this.isDragging) {
        // 拖動裁切框
        let newX = this.cropBoxStart.x + dx;
        let newY = this.cropBoxStart.y + dy;

        // 限制在影片範圍內
        newX = Math.max(0, Math.min(newX, this.videoWidth - this.cropBox.width));
        newY = Math.max(0, Math.min(newY, this.videoHeight - this.cropBox.height));

        this.cropBox.x = newX;
        this.cropBox.y = newY;
      } else if (this.isResizing) {
        // 調整裁切框大小
        this.resize(dx, dy);
      }
    },

    resize(dx, dy) {
      const handle = this.resizeHandle;
      let newBox = { ...this.cropBoxStart };

      if (handle.includes('e')) {
        newBox.width = this.cropBoxStart.width + dx;
      }
      if (handle.includes('w')) {
        newBox.width = this.cropBoxStart.width - dx;
        newBox.x = this.cropBoxStart.x + dx;
      }
      if (handle.includes('s')) {
        newBox.height = this.cropBoxStart.height + dy;
      }
      if (handle.includes('n')) {
        newBox.height = this.cropBoxStart.height - dy;
        newBox.y = this.cropBoxStart.y + dy;
      }

      // 如果有鎖定比例
      if (this.aspectRatio) {
        const currentRatio = newBox.width / newBox.height;
        if (Math.abs(currentRatio - this.aspectRatio) > 0.01) {
          if (handle.includes('e') || handle.includes('w')) {
            newBox.height = newBox.width / this.aspectRatio;
          } else {
            newBox.width = newBox.height * this.aspectRatio;
          }
        }
      }

      // 限制最小尺寸
      const minSize = 50;
      if (newBox.width < minSize || newBox.height < minSize) return;

      // 限制在影片範圍內
      if (newBox.x < 0) {
        newBox.width += newBox.x;
        newBox.x = 0;
      }
      if (newBox.y < 0) {
        newBox.height += newBox.y;
        newBox.y = 0;
      }
      if (newBox.x + newBox.width > this.videoWidth) {
        newBox.width = this.videoWidth - newBox.x;
      }
      if (newBox.y + newBox.height > this.videoHeight) {
        newBox.height = this.videoHeight - newBox.y;
      }

      this.cropBox = newBox;
    },

    stopDrag() {
      this.isDragging = false;
      this.isResizing = false;
      this.resizeHandle = '';
    },

    confirm() {
      this.$emit('confirm', {
        file: this.videoFile,
        cropParams: {
          x: Math.round(this.cropBox.x),
          y: Math.round(this.cropBox.y),
          width: Math.round(this.cropBox.width),
          height: Math.round(this.cropBox.height)
        }
      });
    },

    cancel() {
      this.$emit('cancel');
    }
  }
};
</script>

<style scoped>
.video-cropper {
  padding: 20px;
}

.ratio-selector {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.ratio-btn {
  padding: 8px 16px;
  border: 2px solid #d1d5db;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.ratio-btn:hover {
  border-color: #3b82f6;
  background: #eff6ff;
}

.ratio-btn.active {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.preview-container {
  position: relative;
  display: inline-block;
  margin: 20px 0;
}

.crop-overlay {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
}

.crop-box {
  position: absolute;
  border: 2px solid #3b82f6;
  cursor: move;
  pointer-events: all;
  box-sizing: border-box;
}

.resize-handle {
  position: absolute;
  width: 12px;
  height: 12px;
  background: #3b82f6;
  border: 2px solid white;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0,0,0,0.3);
}

.resize-handle.nw {
  top: -6px;
  left: -6px;
  cursor: nw-resize;
}

.resize-handle.ne {
  top: -6px;
  right: -6px;
  cursor: ne-resize;
}

.resize-handle.sw {
  bottom: -6px;
  left: -6px;
  cursor: sw-resize;
}

.resize-handle.se {
  bottom: -6px;
  right: -6px;
  cursor: se-resize;
}

.crop-info {
  position: absolute;
  top: 10px;
  left: 10px;
  background: rgba(59, 130, 246, 0.9);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  pointer-events: none;
}

.info-panel {
  background: #f3f4f6;
  padding: 15px;
  border-radius: 6px;
  margin: 20px 0;
}

.info-row {
  display: flex;
  justify-content: space-between;
  margin: 5px 0;
  font-size: 14px;
}

.info-row .label {
  color: #6b7280;
  font-weight: 500;
}

.info-row .value {
  color: #111827;
  font-family: monospace;
}

.actions {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 20px;
}

.actions button {
  padding: 12px 30px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
}

.btn-confirm {
  background: #3b82f6;
  color: white;
}

.btn-confirm:hover {
  background: #2563eb;
}

.btn-cancel {
  background: #e5e7eb;
  color: #374151;
}

.btn-cancel:hover {
  background: #d1d5db;
}
</style>
